import {
  CompartmentSelectionStrategy,
  CompartmentSelector,
  CompartmentSelectorConfiguration,
  MinMaxType,
} from '@ekkogmbh/apisdk';
import { CheckCircle as Check, Delete, ExpandLess, ExpandMore, HighlightOff as Close } from '@mui/icons-material';
import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  Box,
  FormControl,
  FormControlLabel,
  IconButton,
  ListItemText,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell as MuiTableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListForm } from 'src/Common/Components/Forms/ListForm';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CoordinateInput } from '../../Common/Components/CoordinateInput';
import { StyledSelectField } from '../../Common/Components/Forms/StyledSelectField';
import { ContentPanel, StepDefinition, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import ObjectField, { ObjectFieldItem } from 'src/Common/Components/DataTable/ObjectField';
import NamedCoordinateDescription from 'src/Common/Components/Stepper/NameCoordinateDescription';
import { CenteredColumn, ReactivePanelColumn, ReactiveRow } from 'src/Common/Components/Stacks';
import { BrightTypography } from 'src/Common/Components/Typographies';
import { ExampleBox } from 'src/Common/Components/Forms/ExampleBox';
import CompartmentSelectorConfigurationField from './CompartmentSelectorConfigurationField';
import { invalidCoordinatePrefix } from 'src/Common/Helper/Validation';
import RegexInput from 'src/Common/Components/Forms/RegexInput';

const Accordion = styled(MuiAccordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

const ConfigInput = ({ children, helpText }: { children: React.JSX.Element; helpText: React.JSX.Element }) => (
  <ReactiveRow spacing={2}>
    <CenteredColumn width={'100%'}>{children}</CenteredColumn>
    <Box mr={{ md: 4 }}>{helpText}</Box>
  </ReactiveRow>
);

interface Props {
  compartmentSelector?: CompartmentSelector;
  closeHandler: () => void;
  saveHandler: (compartmentSelector: CompartmentSelector) => void;
}

const Component = ({ compartmentSelector, closeHandler, saveHandler }: Props): React.JSX.Element => {
  const { currentStep, enableNext, enableSave } = usePanel();
  const [loading, setLoading] = useState<boolean>(true);
  const [expanded, setExpanded] = useState<number>(0);

  const [name, setName] = useState<string>('');
  const [coordinate, setCoordinate] = useState<string>('');
  const [type, setType] = useState<CompartmentSelectionStrategy>(CompartmentSelectionStrategy.COORDINATE);
  const [configuration, setConfiguration] = useState<CompartmentSelectorConfiguration>({});

  const payload = useMemo(() => {
    const isValueHasFields =
      type === CompartmentSelectionStrategy.VALUE && configuration.fields && configuration.fields.length > 0;

    const isCoordinatePrefixValid =
      type === CompartmentSelectionStrategy.COORDINATE &&
      (configuration.prefix === undefined || !invalidCoordinatePrefix(coordinate, configuration.prefix));
    if ([name, coordinate].includes('') || !(isValueHasFields || isCoordinatePrefixValid)) {
      return undefined;
    }

    return { name, coordinate, type, configuration } as CompartmentSelector;
  }, [name, coordinate, type, configuration]);

  const reset = useCallback(() => {
    setName(compartmentSelector?.name ?? '');
    setCoordinate(compartmentSelector?.coordinate ?? '');
    setType(compartmentSelector?.type ?? CompartmentSelectionStrategy.COORDINATE);
    setConfiguration(compartmentSelector?.configuration ?? {});
  }, [compartmentSelector]);

  /** on mount */
  useEffect(() => reset(), [reset]);

  /** reactive panel effects */
  useEffect(() => {
    const { batch } = configuration;

    const hasId = name !== '' && coordinate !== '';
    const hasType = batch?.delimiter !== '';

    switch (currentStep) {
      case 0: {
        enableNext(hasId);
        break;
      }
      case 1: {
        enableNext(hasType);
        break;
      }
      default: {
        enableSave(hasId && hasType && !!payload);
        enableNext(true);
      }
    }
  }, [currentStep, enableNext, enableSave, name, coordinate, configuration, payload]);

  const onSave = useCallback(() => {
    if (payload === undefined) {
      return;
    }
    saveHandler(payload);
  }, [payload, saveHandler, closeHandler, setLoading]);

  const setConfigurationValue = useCallback(
    (key: keyof CompartmentSelectorConfiguration, value: string | string[] | Record<string, string | number>) => {
      if (
        key !== 'batch' &&
        (value === '' ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === 'object' && Object.keys(value).length === 0) ||
          (typeof value === 'object' && Object.values(value).every((v) => v === '')))
      ) {
        delete configuration[key];
        setConfiguration({ ...configuration });
        return;
      }

      const newConfig = {
        ...configuration,
        [key]: value,
      };

      setConfiguration(newConfig);
    },
    [configuration],
  );

  const constantConfig = useMemo(
    () => (
      <ConfigInput
        helpText={
          <ExampleBox
            parameters={[{ key: 'Constant', value: 'always-the-same' }]}
            examples={[{ input: 'text', result: 'always-the-same-text' }]}
          />
        }
      >
        <StyledTextField
          type={'text'}
          label={'Value'}
          value={configuration.constant as string}
          onChange={(e) => setConfigurationValue('constant', e.target.value as string)}
        />
      </ConfigInput>
    ),
    [configuration.constant, setConfigurationValue],
  );

  const prefixConfig = useMemo(() => {
    const { prefix } = configuration;
    const isByValue = type === CompartmentSelectionStrategy.VALUE;

    return (
      <ConfigInput
        helpText={
          <ExampleBox
            parameters={[{ key: 'Prefix', value: 'prefixed' }]}
            examples={[
              {
                input: 'prefixed',
                result: (
                  <BrightTypography>
                    <Stack direction={'row'}>
                      <Typography>text</Typography>
                      <Typography color={'textSecondary'} sx={{ fontStyle: 'italic' }}>
                        prefixed
                      </Typography>
                    </Stack>
                  </BrightTypography>
                ),
              },
            ]}
          />
        }
      >
        {isByValue ? (
          <StyledTextField
            type={'text'}
            label={'Prefix'}
            value={prefix ?? ''}
            onChange={(e) => setConfigurationValue('prefix', e.target.value as string)}
          />
        ) : (
          <CoordinateInput
            label="Prefix"
            value={prefix ?? ''}
            onChange={(coordinate) => setConfigurationValue('prefix', coordinate)}
            trailingDelimiter="both"
          />
        )}
      </ConfigInput>
    );
  }, [setConfigurationValue, configuration.prefix, type]);

  const suffixConfig = useMemo(() => {
    const { suffix } = configuration;

    return (
      <ConfigInput
        helpText={
          <ExampleBox
            parameters={[{ key: 'Suffixed', value: 'suffixed' }]}
            examples={[
              {
                input: 'text',
                result: (
                  <BrightTypography>
                    <Stack direction={'row'}>
                      <Typography>text</Typography>
                      <Typography color={'textSecondary'} sx={{ fontStyle: 'italic' }}>
                        suffixed
                      </Typography>
                    </Stack>
                  </BrightTypography>
                ),
              },
            ]}
          />
        }
      >
        <StyledTextField
          type={'text'}
          label={'Value'}
          value={suffix as string}
          onChange={(e) => setConfigurationValue('suffix', e.target.value as string)}
        />
      </ConfigInput>
    );
  }, [setConfigurationValue, configuration.suffix]);

  const fieldsConfig = useMemo(() => {
    const { fields } = configuration;

    return (
      <ConfigInput
        helpText={
          <ExampleBox
            parameters={[{ key: 'Fields', value: 'Amount' }]}
            examples={[{ input: '15', result: 'X_660_B' }]}
            additionalInfo={
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <MuiTableCell>Coordinate</MuiTableCell>
                      <MuiTableCell>Name</MuiTableCell>
                      <MuiTableCell>Amount</MuiTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <MuiTableCell>X_660_A</MuiTableCell>
                      <MuiTableCell>Schraube</MuiTableCell>
                      <MuiTableCell>30</MuiTableCell>
                    </TableRow>
                    <TableRow>
                      <MuiTableCell>X_660_B</MuiTableCell>
                      <MuiTableCell>Schraube</MuiTableCell>
                      <MuiTableCell>15</MuiTableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            }
          />
        }
      >
        <ListForm
          items={(fields as string[]) ?? []}
          onChange={(values) => setConfigurationValue('fields', [...values])}
        />
      </ConfigInput>
    );
  }, [setConfigurationValue, configuration.fields]);

  const splitConfig = useMemo(() => {
    const { split } = configuration;
    const delimiter = split?.delimiter ?? '';
    const position = split?.position ?? 1;

    return (
      <ConfigInput
        helpText={
          <ExampleBox
            parameters={[
              { key: 'Delimiter', value: ';' },
              { key: 'Position', value: '3' },
            ]}
            examples={[
              {
                input: 'split;this;text;',
                result: 'text',
              },
            ]}
            description={
              <>
                Split your input text at the delimiter character and select the required part by position. The first
                part has the position 1.
              </>
            }
          />
        }
      >
        <CenteredColumn width={'100%'}>
          <StyledTextField
            type={'text'}
            label={'Delimiter'}
            value={delimiter}
            onChange={(e) =>
              setConfigurationValue('split', {
                position,
                delimiter: e.target.value as string,
              })
            }
          />
          <StyledTextField
            numeric
            type={'number'}
            label={'Position'}
            value={position}
            onChange={(e) => {
              const intValue = parseInt(e.target.value);
              const newValue = intValue >= 1 ? intValue : 1;

              setConfigurationValue('split', {
                delimiter,
                position: newValue,
              });
            }}
          />
        </CenteredColumn>
      </ConfigInput>
    );
  }, [setConfigurationValue, configuration.split]);

  const regexConfig = useMemo(() => {
    const { regex } = configuration;
    const pattern = regex?.pattern ?? '';
    const replacement = regex?.replacement ?? '';

    return (
      <ConfigInput
        helpText={
          <ExampleBox
            parameters={[
              { key: 'Pattern', value: '/([0-9]{3})/' },
              { key: 'Replacement', value: 'replacement$1' },
            ]}
            examples={[
              {
                input: 'input1234',
                result: 'replacement123',
              },
            ]}
            description={
              <>
                (Programming skills recommended)
                <br />
                Use a regular expression to split your input in groups.
                <br />
                Use groups in your replacement text to generate the new input.
                <br />
                Learn more about regular expressions on https://regex101.com/
              </>
            }
          />
        }
      >
        <RegexInput
          label={'Pattern'}
          value={pattern}
          replacement={replacement}
          onChange={(value) => setConfigurationValue('regex', { replacement, pattern: value })}
          onChangeReplacement={(value) => setConfigurationValue('regex', { replacement: value, pattern })}
        />
      </ConfigInput>
    );
  }, [setConfigurationValue, configuration.regex]);

  const minmaxConfig = useMemo(() => {
    const { minmax } = configuration;

    const type = minmax?.type ?? MinMaxType.MAX;
    const field = minmax?.field ?? '';

    return (
      <ConfigInput
        helpText={
          <ExampleBox
            parameters={[
              { key: 'Field', value: 'Amount' },
              { key: 'Type', value: 'maximize | minimize' },
            ]}
            examples={[
              {
                contextInfo: 'maximize',
                input: 'Schraube',
                result: (
                  <Stack>
                    <Box sx={{ display: 'flex', p: 2 }}>
                      <Stack direction={'row'} alignItems={'center'} sx={{ pl: 3 }}>
                        <Typography sx={{ px: 1, fontWeight: 700, fontSize: '2rem' }}>→</Typography>
                        <BrightTypography sx={{ mr: 1 }}>X_660_A</BrightTypography>
                        <BrightTypography>X_660_B</BrightTypography>
                      </Stack>
                    </Box>
                    <Box sx={{ display: 'flex', p: 2 }}>
                      <Stack direction={'row'} alignItems={'center'} sx={{ pl: 3 }}>
                        <Typography sx={{ px: 1, fontWeight: 700, fontSize: '2rem' }}>→</Typography>
                        <BrightTypography>X_660_C</BrightTypography>
                      </Stack>
                    </Box>
                  </Stack>
                ),
              },
              {
                contextInfo: 'minimize',
                input: 'Schraube',
                result: (
                  <Stack>
                    <Box sx={{ display: 'flex', p: 2 }}>
                      <Stack direction={'row'} alignItems={'center'} sx={{ pl: 3 }}>
                        <Typography sx={{ px: 1, fontWeight: 700, fontSize: '2rem' }}>→</Typography>
                        <BrightTypography sx={{ mr: 1 }}>X_660_A</BrightTypography>
                        <BrightTypography>X_660_B</BrightTypography>
                      </Stack>
                    </Box>
                    <Box sx={{ display: 'flex', p: 2 }}>
                      <Stack direction={'row'} alignItems={'center'} sx={{ pl: 3 }}>
                        <Typography sx={{ px: 1, fontWeight: 700, fontSize: '2rem' }}>→</Typography>
                        <BrightTypography>X_660_C</BrightTypography>
                      </Stack>
                    </Box>
                  </Stack>
                ),
              },
            ]}
            additionalInfo={
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <MuiTableCell>Coordinate</MuiTableCell>
                      <MuiTableCell>Name</MuiTableCell>
                      <MuiTableCell>Amount</MuiTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <MuiTableCell>X_660_A</MuiTableCell>
                      <MuiTableCell>Schraube</MuiTableCell>
                      <MuiTableCell>30</MuiTableCell>
                    </TableRow>
                    <TableRow>
                      <MuiTableCell>X_660_B</MuiTableCell>
                      <MuiTableCell>Schraube</MuiTableCell>
                      <MuiTableCell>30</MuiTableCell>
                    </TableRow>
                    <TableRow>
                      <MuiTableCell>X_660_C</MuiTableCell>
                      <MuiTableCell>Schraube</MuiTableCell>
                      <MuiTableCell>5</MuiTableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            }
          />
        }
      >
        <CenteredColumn width={'100%'}>
          <StyledSelectField
            label={'Type'}
            value={type}
            onChange={(e) =>
              setConfigurationValue('minmax', {
                field,
                type: e.target.value as MinMaxType,
              })
            }
          >
            {Object.values(MinMaxType).map((m, i) => (
              <MenuItem key={`minmax-item-${i}`} value={m}>
                <ListItemText primary={m} />
              </MenuItem>
            ))}
          </StyledSelectField>
          <StyledTextField
            type={'text'}
            label={'Field'}
            value={field}
            onChange={(e) => setConfigurationValue('minmax', { type, field: e.target.value as string })}
            endAdornment={
              <IconButton
                disabled={configuration.minmax === undefined}
                onClick={() => {
                  delete configuration['minmax'];
                  setConfiguration({ ...configuration });
                }}
              >
                <Delete />
              </IconButton>
            }
          />
        </CenteredColumn>
      </ConfigInput>
    );
  }, [setConfigurationValue, configuration.minmax]);

  const inputValidationConfig = useMemo(() => {
    const { inputValidation } = configuration;

    const pattern = inputValidation?.pattern ?? '';
    const errorText = inputValidation?.errorText ?? '';

    return (
      <ConfigInput
        helpText={
          <ExampleBox
            parameters={[
              { key: 'Pattern', value: '/[a-zA-Z0-9]{8}/' },
              { key: 'ErrorText', value: 'Invalid input' },
            ]}
            examples={[
              { input: 'input123', result: 'success' },
              {
                input: 'input1234',
                result: (
                  <BrightTypography sx={(theme) => ({ color: theme.palette.error.main })}>
                    Invalid Input
                  </BrightTypography>
                ),
              },
            ]}
            description={
              <>
                (Programming skills recommended)
                <br />
                Use a regular expression to validate your input.
                <br />
                If the validation fails your error message will be displayed to the user.
                <br />
                For the validation the match of the regular expression must be equal to the input.
              </>
            }
          />
        }
      >
        <CenteredColumn width={'100%'}>
          <RegexInput
            label={'Pattern'}
            value={pattern}
            onChange={(value) => {
              setConfigurationValue('inputValidation', {
                pattern: value,
                errorText,
              });
            }}
          />
          <StyledTextField
            type={'text'}
            label={'Error Text'}
            value={errorText}
            onChange={(e) =>
              setConfigurationValue('inputValidation', {
                errorText: e.target.value as string,
                pattern,
              })
            }
          />
        </CenteredColumn>
      </ConfigInput>
    );
  }, [setConfigurationValue, configuration.inputValidation]);

  const batchConfig = useMemo(() => {
    const { batch } = configuration;

    return (
      <Box width={'100%'}>
        <ConfigInput
          helpText={
            <ExampleBox
              parameters={[{ key: 'Delimiter', value: ';' }]}
              examples={[
                {
                  input: 'input1;input2;input3',
                  result: (
                    <Stack direction={'row'} spacing={1}>
                      <BrightTypography>input1</BrightTypography>
                      <BrightTypography>input2</BrightTypography>
                      <BrightTypography>input3</BrightTypography>
                    </Stack>
                  ),
                },
              ]}
            />
          }
        >
          <Box sx={{ pl: 10 }}>
            <Box sx={{ pb: 2 }}>Split the input text by a delimiter and use each part as its own input.</Box>
            <FormControl>
              <RadioGroup
                aria-labelledby="radio-group"
                value={batch !== undefined ? 'multiple' : 'single'}
                name="radio-group"
                onChange={(e) => {
                  if (e.target.value === 'single') {
                    delete configuration['batch'];
                    setConfiguration({ ...configuration });
                  } else {
                    setConfigurationValue('batch', { delimiter: '' });
                  }
                }}
              >
                <FormControlLabel value="single" control={<Radio />} label="Single" />
                <FormControlLabel value="multiple" control={<Radio />} label="Multiple" />
              </RadioGroup>
            </FormControl>
            {batch !== undefined && (
              <StyledTextField
                type={'text'}
                label={'delimiter'}
                value={batch?.delimiter}
                onChange={(e) => {
                  setConfigurationValue('batch', { delimiter: e.target.value as string });
                }}
              />
            )}
          </Box>
        </ConfigInput>
      </Box>
    );
  }, [setConfigurationValue, configuration.batch]);

  const configEntry = useCallback(
    (children: React.JSX.Element, configKey: string, description: string, order: number, label?: string) => {
      const isExpanded = expanded === order;
      const isEnabled = configuration[configKey as keyof CompartmentSelectorConfiguration] !== undefined;
      const labelNode = <Typography>{label ?? configKey.slice(0, 1).toUpperCase() + configKey.slice(1)}</Typography>;

      return (
        <Accordion
          key={`config-accordion-${configKey}`}
          disableGutters
          square
          elevation={0}
          expanded={isExpanded}
          onChange={() => setExpanded(isExpanded ? 0 : order)}
        >
          <AccordionSummary expandIcon={null}>
            <ReactiveRow width={'100%'} spacing={4}>
              <Stack direction={'row'}>
                {isEnabled ? (
                  <Check color={'secondary'} sx={{ fontSize: 32 }} />
                ) : (
                  <Close color={'secondary'} sx={{ fontSize: 32 }} />
                )}
                {isExpanded ? (
                  <ExpandLess color={'secondary'} sx={{ fontSize: 32, ml: 4 }} />
                ) : (
                  <ExpandMore color={'secondary'} sx={{ fontSize: 32, ml: 4 }} />
                )}
                <Box sx={{ alignSelf: 'center', pl: 2, flex: 1 }}>{labelNode}</Box>
              </Stack>
              <Box sx={{ px: 2 }}>
                <Typography variant={'caption'}>{description}</Typography>
              </Box>
            </ReactiveRow>
          </AccordionSummary>
          <AccordionDetails>{children}</AccordionDetails>
        </Accordion>
      );
    },
    [expanded, configuration],
  );

  const configurationStep: StepDefinition = useMemo(
    () => ({
      title: 'Configuration',
      description: <CompartmentSelectorConfigurationField configuration={configuration} />,
      contentCallback: () => (
        <>
          {configEntry(prefixConfig, 'prefix', 'Always add a constant text in front of your input.', 10)}
          {configEntry(
            constantConfig,
            'constant',
            'Will always use the constant text and ignore the actual input.',
            20,
          )}
          {configEntry(suffixConfig, 'suffix', 'Always add a constant text behind your input.', 30)}
          {configEntry(
            splitConfig,
            'split',
            'Splits the selector input and uses only the value at the given position.',
            40,
          )}
          {configEntry(regexConfig, 'regex', 'Use regular expression pattern/replacement to transform the input.', 50)}
          {configEntry(
            inputValidationConfig,
            'inputValidation',
            'Limit accepted inputs by matching against a regex pattern.',
            60,
            'Validation',
          )}
          {type === CompartmentSelectionStrategy.VALUE && (
            <>
              {configEntry(fieldsConfig, 'fields', 'Match against compartment fields for the selection.', 5)}
              {configEntry(
                minmaxConfig,
                'minmax',
                'Reduce your found matches by only selecting the one with the highest or lowest value in a certain field.',
                90,
                'Min-Max',
              )}
            </>
          )}
        </>
      ),
    }),
    [
      configEntry,
      configuration,
      type,
      prefixConfig,
      constantConfig,
      suffixConfig,
      splitConfig,
      regexConfig,
      inputValidationConfig,
      fieldsConfig,
      minmaxConfig,
    ],
  );

  const identifierStep: StepDefinition = useMemo(() => {
    const isDisabled = compartmentSelector !== undefined;

    return {
      title: 'Identifier',
      description: <NamedCoordinateDescription name={name} coordinate={coordinate} />,
      contentCallback: () => (
        <ReactivePanelColumn>
          <StyledTextField
            type={'text'}
            label={'Name'}
            value={name}
            disabled={isDisabled}
            onChange={(e) => {
              setName(String(e.target.value));
            }}
          />
          <CoordinateInput
            value={coordinate}
            disabled={isDisabled}
            onChange={(c) => {
              if (['', undefined].includes(configuration.prefix) && type === CompartmentSelectionStrategy.COORDINATE) {
                setConfigurationValue('prefix', c);
              }
              setCoordinate(c);
            }}
            trailingDelimiter={false}
          />
        </ReactivePanelColumn>
      ),
    };
  }, [compartmentSelector, name, coordinate, configuration.prefix, type, setConfigurationValue]);

  const batchStep: StepDefinition = useMemo(() => {
    const { batch } = configuration;

    const isBatch = batch !== undefined;

    const configItems: ObjectFieldItem[] = [
      {
        key: 'Type',
        value: isBatch ? 'Multiple' : 'Single',
      },
    ];

    if (isBatch) {
      configItems.push({
        key: 'Batch Delimiter',
        value: batch?.delimiter ?? '',
      });
    }

    return {
      title: 'Batch',
      contentCallback: () => (
        <Box sx={{ display: 'flex', flex: 1, alignItems: 'center', width: '100%' }}>{batchConfig}</Box>
      ),
      description: <ObjectField direction={'column'} fields={configItems} />,
    };
  }, [batchConfig, configuration.batch]);

  const selectorTypeStep: StepDefinition = useMemo(() => {
    const { prefix } = configuration;

    const typeOptions = Object.values(CompartmentSelectionStrategy).map((value, index) => (
      <option key={`selector-strategy-${index}`} value={value}>
        {value}
      </option>
    ));

    return {
      title: 'Selector-Type',
      contentCallback: () => (
        <ReactivePanelColumn>
          <StyledSelectField
            native
            value={type}
            onChange={(e) => {
              const newType = e.target.value as CompartmentSelectionStrategy;

              if (newType === CompartmentSelectionStrategy.COORDINATE && prefix === undefined) {
                setConfigurationValue('prefix', coordinate);
              } else if (newType === CompartmentSelectionStrategy.VALUE) {
                delete configuration['prefix'];
                setConfiguration({ ...configuration });
              }

              setType(newType);
            }}
            label="Type"
          >
            {typeOptions}
          </StyledSelectField>
        </ReactivePanelColumn>
      ),
      description: <Typography color={'secondary'}>{type}</Typography>,
    };
  }, [type, configuration.prefix, setConfigurationValue]);

  return (
    <ContentPanel
      isLoading={loading}
      initialStep={compartmentSelector !== undefined ? 1 : 0}
      steps={[identifierStep, batchStep, selectorTypeStep, configurationStep]}
      onCancel={closeHandler}
      onReset={reset}
      onSave={onSave}
    />
  );
};

export const CompartmentSelectorPanel = Component;
